import {
  Box,
  Chip,
  IconButton,
  LinearProgress,
  Typography
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { Section } from "components/disw";
import BaseButton from "components/disw/BaseButton";
import { DropDownBox } from "components/disw/DropDownBox";
import { ErrorBox } from "components/error";
import {
  RestrictedSolutionRealm,
  SolutionPermission
} from "features/access-management";
import { useMemo, useState } from "react";
import {
  useChangeDataSourceAccess,
  useDataSourceApiKeys
} from "./hooks/useDataSources";
import editIcon from "./icons/edit.svg";

const useStyles = makeStyles({
  editButton: {
    borderRadius: 0,
    width: 36,
    height: 28,
    "&:hover": { background: "transparent" },
    "&:focus-visible": { outline: "2px solid #0F789B" }
  }
});

interface DataSourceAccessProps {
  solutionKey: string;
  dataSourceKey: string;
  access: string[];
  onChanged: () => Promise<unknown>;
}

export default function DataSourceAccess({
  solutionKey,
  dataSourceKey,
  access,
  onChanged
}: DataSourceAccessProps) {
  const classes = useStyles();
  const [editing, setEditing] = useState(false);
  const [selection, setSelection] = useState<string[]>(access);
  const [saving, setSaving] = useState(false);
  const apiKeysQuery = useDataSourceApiKeys();
  const [changeAccess, { error, reset }] = useChangeDataSourceAccess();

  const apiKeys = useMemo(
    () => apiKeysQuery.data?.publicApi.apiKeys.apiKeys ?? [],
    [apiKeysQuery.data]
  );
  const nameOf = (id: string) =>
    apiKeys.find(apiKey => apiKey.id === id)?.name ?? id;
  // Stable object references are required for the DX list selection
  const options = useMemo(
    () => [
      ...apiKeys.map(({ id, name }) => ({ id, name })),
      ...access
        .filter(id => !apiKeys.some(apiKey => apiKey.id === id))
        .map(id => ({ id, name: id }))
    ],
    [apiKeys, access]
  );
  const selectedOptions = useMemo(
    () => options.filter(option => selection.includes(option.id)),
    [options, selection]
  );
  // The DX list re-emits its selection on every prop update; ignore unchanged selections to avoid a render loop
  const changeSelection = (value: { id: string }[]) => {
    const ids = value.map(option => option.id);
    if (
      ids.length !== selection.length ||
      ids.some(id => !selection.includes(id))
    )
      setSelection(ids);
  };

  const startEditing = () => {
    reset();
    setSelection(access);
    setEditing(true);
  };

  const save = async () => {
    const granted = selection.filter(id => !access.includes(id));
    const revoked = access.filter(id => !selection.includes(id));
    setSaving(true);
    try {
      for (const [apiKeyId, hasAccess] of [
        ...granted.map(id => [id, true] as const),
        ...revoked.map(id => [id, false] as const)
      ]) {
        await changeAccess({
          variables: {
            solutionKey,
            dataSourceKey,
            apiKeyId,
            access: hasAccess
          }
        });
      }
      setEditing(false);
    } catch {
      // the error is rendered from the mutation result
    } finally {
      await onChanged();
      setSaving(false);
    }
  };

  return (
    <Section
      name="API keys"
      actions={
        !editing && (
          <RestrictedSolutionRealm
            silent
            requiredPermissions={[SolutionPermission.ChangeCloudDataSources]}
          >
            <IconButton
              className={classes.editButton}
              aria-label="Edit"
              onClick={startEditing}
              data-testid="data-source-access-edit"
            >
              <img src={editIcon} alt="Edit" width={16} height={16} />
            </IconButton>
          </RestrictedSolutionRealm>
        )
      }
    >
      <Box display="flex" flexDirection="column" gap={2}>
        {editing ? (
          <div data-testid="data-source-access-input">
            <DropDownBox
              label="API keys with access"
              placeholder="Select API keys"
              dataSource={options}
              value={selectedOptions}
              onValueChanged={({ value }) => changeSelection(value)}
              displayExpr="name"
              readOnly={saving}
            />
            {apiKeysQuery.loading && <LinearProgress />}
          </div>
        ) : access.length ? (
          <Box display="flex" flexWrap="wrap" gap={1}>
            {access.map(id => (
              <Chip
                key={id}
                label={nameOf(id)}
                size="small"
                data-testid={`data-source-access-${id}`}
              />
            ))}
          </Box>
        ) : (
          <Typography variant="body2">
            No API keys have access to this data source.
          </Typography>
        )}
        {saving && <LinearProgress />}
        {error && (
          <ErrorBox
            apolloError={error}
            title="Unable to change API key access"
          />
        )}
        {editing && (
          <Box display="flex" justifyContent="flex-end" gap={1}>
            <BaseButton
              text="Cancel"
              styleType="default"
              onClick={() => setEditing(false)}
              disabled={saving}
              data-testid="data-source-access-cancel"
            />
            <BaseButton
              text="Save"
              onClick={save}
              disabled={saving}
              data-testid="data-source-access-save"
            />
          </Box>
        )}
      </Box>
    </Section>
  );
}
